import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogoLockup } from "@/components/Logo";
import GoogleLoginButton from "@/components/GoogleLoginButton";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const next = new URLSearchParams(loc.search).get("next") || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}!`);
      const dest = u.role === "admin" ? "/admin" : u.role === "owner" ? "/owner" : (next && next !== "/" ? next : "/dashboard");
      nav(dest);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FBF5EA] flex">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center mb-10">
            <LogoLockup size={44} tld={false} />
          </Link>
          <h1 className="font-display font-black text-4xl tracking-tighter">Welcome back.</h1>
          <p className="mt-2 text-slate-600">Log in to book and manage your stay.</p>

          <div className="mt-8">
            <GoogleLoginButton label="Continue with Google" />
            <div className="my-4 flex items-center gap-3">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[10px] tracking-widest uppercase font-bold text-slate-400">or with email</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs tracking-widest uppercase font-bold">Email</Label>
              <Input data-testid="login-email" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required className="mt-1 h-12" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs tracking-widest uppercase font-bold">Password</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-orange-800 font-semibold hover:underline">Forgot password?</Link>
              </div>
              <Input data-testid="login-password" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 h-12" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-12 rounded-full bg-orange-800 hover:bg-orange-800 text-white active:scale-95 transition-transform">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-slate-600">New here? <Link to="/signup" className="text-orange-800 font-semibold hover:underline">Create an account</Link></div>
        </div>
      </div>
      <div className="hidden lg:block flex-1 bg-orange-800 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1783254877023-f27a32c7e8bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBzdHVkZW50JTIwc3R1ZHlpbmclMjBob3N0ZWx8ZW58MHx8fHwxNzgzNjU5OTc2fDA&ixlib=rb-4.1.0&q=85" className="w-full h-full object-cover opacity-70" alt="" />
        <div className="absolute inset-0 bg-orange-900/40" />
        <div className="absolute bottom-16 left-16 right-16 text-white">
          <div className="text-xs tracking-[0.25em] uppercase font-bold text-amber-400">Trusted by students</div>
          <div className="font-display font-black text-4xl tracking-tighter mt-2">Beds from ₹250 near every major coaching center.</div>
        </div>
      </div>
    </div>
  );
}
